import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Loader2, Search, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/**
 * How a checked row reads in an excluded column: a red cross rather than a
 * check, the cross the exclude chips carry elsewhere.
 */
const EXCLUDE_MARK = {
  icon: X,
  className:
    'data-[state=checked]:border-destructive data-[state=checked]:bg-destructive data-[state=checked]:text-white dark:data-[state=checked]:bg-destructive',
}

/**
 * How many rows a column renders at a time. A column can hold thousands, and
 * rendering them all at once stalls the browser, so more are added as the
 * column is scrolled.
 */
const ROWS_PER_PAGE = 100

/** How close to the bottom, in pixels, the next rows are added. */
const LOAD_MORE_MARGIN = 200

/** One row of a picker column. */
export interface PickerColumnRow {
  id: string
  name: string
  /** Second line under the name, e.g. the site a unit belongs to. */
  sub?: string
  /** Right-aligned figure, e.g. how many children the row has. */
  meta?: React.ReactNode
  /** False for a row with nothing inside it, e.g. a postcode. */
  openable?: boolean
}

export interface PickerColumnProps {
  /** Column heading, e.g. "Publishers". */
  label: string
  rows: PickerColumnRow[]
  selectedIds: string[]
  onToggle: (id: string, selected: boolean) => void
  searchPlaceholder: string
  /** A term applied on top of the column's own search box. */
  filter?: string
  loading?: boolean
  emptyText: string
  /**
   * Opens a row's own contents in the column to the right. Given, the row's
   * name becomes a button and the checkbox is left to do the selecting alone —
   * so a place can be walked into without being targeted.
   */
  onOpen?: (id: string) => void
  /** The row whose contents the next column is showing. */
  openId?: string
  /** Rendered under the rows, e.g. a count of what the column is not showing. */
  footer?: React.ReactNode
  /** Rendered beside the heading, above the search box, e.g. an include/exclude switch. */
  toolbar?: React.ReactNode
  /** The selected rows are excluded rather than targeted. */
  negated?: boolean
  /**
   * What the rows are, e.g. "ad units". Given, the column says under its search
   * box what its selection amounts to, e.g. "All 34 ad units" while nothing is
   * picked.
   */
  what?: string
  /** False when the rows are only part of what exists, so no total is given. */
  complete?: boolean
  /** Offers a link beside the summary to show only the selected rows. */
  showSelectedToggle?: boolean
  /**
   * False for a column that is only walked through, never picked from: its rows
   * have no checkbox, and need `onOpen` to do anything at all.
   */
  selectable?: boolean
  /**
   * Hands the search box's text to the caller, for a column whose rows are
   * searched on the server; the rows given are then shown as they are.
   */
  onSearch?: (term: string) => void
  className?: string
}

/**
 * A searchable column of checkbox rows. Several side by side make a cascading
 * browser: each column lists what the selections to its left leave standing.
 */
export function PickerColumn({
  label,
  rows,
  selectedIds,
  onToggle,
  searchPlaceholder,
  filter = '',
  loading,
  emptyText,
  onOpen,
  openId,
  footer,
  toolbar,
  negated,
  what,
  complete = true,
  showSelectedToggle,
  selectable = true,
  onSearch,
  className,
}: PickerColumnProps) {
  const { t } = useTranslation()
  const [term, setTerm] = useState('')
  const [onlySelectedPicked, setOnlySelected] = useState(false)

  const selected = new Set(selectedIds)
  // Turns itself off once nothing is picked, rather than leaving an empty column.
  const onlySelected = onlySelectedPicked && selected.size > 0

  const visible = useMemo(() => {
    const picked = new Set(selectedIds)
    const shown = onlySelected ? rows.filter((row) => picked.has(row.id)) : rows
    // A column searched on the server has had its rows narrowed already.
    const terms = [onSearch ? '' : term, filter]
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean)
    if (!terms.length) return shown
    return shown.filter((row) => {
      const haystack = `${row.name} ${row.sub ?? ''}`.toLowerCase()
      return terms.every((value) => haystack.includes(value))
    })
  }, [rows, term, filter, onlySelected, selectedIds, onSearch])

  // A new search starts from the top again. A change to the rows does not, so
  // ticking a row far down the column leaves it where it is.
  const query = `${term}\n${filter}\n${onlySelected}`
  const [paging, setPaging] = useState({ query, limit: ROWS_PER_PAGE })
  const limit = paging.query === query ? paging.limit : ROWS_PER_PAGE
  const rendered = visible.length > limit ? visible.slice(0, limit) : visible
  const allRendered = rendered.length === visible.length

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (allRendered) return
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_MARGIN)
      setPaging({ query, limit: limit + ROWS_PER_PAGE })
  }

  // Once anything is picked the rest of the column greys out, leaving the
  // picked rows standing out in white. Excluded picks turn that around: they
  // grey out and the rest stays white, so what is targeted is always white.
  const dimmed = (selected.size > 0 && !negated) || !!openId

  // Nothing picked means every row, which an unticked column does not say by
  // itself. A total is only given when the rows are all there is.
  const summary = (() => {
    if (!what || loading || !rows.length) return undefined
    const picked = selected.size
    const count = rows.length
    if (!picked) {
      return complete
        ? t('targeting.column.all', { count, what })
        : t('targeting.column.allUncounted', { what })
    }
    const key = negated ? 'allBut' : 'some'
    return complete && picked <= count
      ? t(`targeting.column.${key}`, { picked, count, what })
      : t(`targeting.column.${key}Uncounted`, { picked, what })
  })()

  return (
    // Fills its slot and no more, so the rows scroll inside the column rather
    // than every column in the browser scrolling together.
    <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col', className)}>
      <div className="flex flex-col gap-2 border-b p-2">
        {/* The toolbar shares the heading's line, and drops under it only when
            the column is too narrow for both. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="px-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            {label}
          </span>
          {toolbar}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={term}
            onChange={(event) => {
              setTerm(event.target.value)
              onSearch?.(event.target.value)
            }}
            placeholder={searchPlaceholder}
            className="h-8 pl-7 text-xs"
          />
        </div>
        {(summary || (showSelectedToggle && selected.size > 0)) && (
          <div className="flex flex-wrap items-center gap-x-2 px-1">
            {summary && <p className="text-xs text-muted-foreground">{summary}</p>}
            {showSelectedToggle && selected.size > 0 && (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="ml-auto h-auto p-0 text-xs"
                onClick={() => setOnlySelected(!onlySelected)}
              >
                {onlySelected ? t('targeting.column.showAll') : t('targeting.column.showSelected')}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Relative so it clips the hidden native input Radix puts beside each
        checkbox inside a form; left to a far ancestor, those inputs sit at
        their rows' unscrolled positions and stretch the page. */}
      <div
        className={cn('relative min-h-0 flex-1 overflow-y-auto', dimmed && 'bg-muted/60')}
        onScroll={onScroll}
      >
        {loading && (
          <div className="flex items-center justify-center p-4">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && visible.length === 0 && (
          <p className="p-3 text-xs text-muted-foreground">
            {rows.length === 0 ? emptyText : t('targeting.noResults')}
          </p>
        )}
        {!loading &&
          rendered.map((row) => {
            const checked = selected.has(row.id)
            const excluded = checked && negated
            const open = onOpen && row.openable !== false
            const mark = negated ? EXCLUDE_MARK : {}
            const name = (
              <span className="min-w-0 flex-1">
                <span className="block truncate">{row.name}</span>
                {row.sub && (
                  <span className="block truncate text-xs text-muted-foreground">{row.sub}</span>
                )}
              </span>
            )
            const meta = row.meta && (
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {row.meta}
              </span>
            )
            const rowClass = cn(
              'flex items-center gap-2 border-b px-3 py-2 text-sm last:border-b-0',
              dimmed ? 'hover:bg-card/60' : 'hover:bg-accent/50',
              (checked || openId === row.id) && 'bg-card hover:bg-card',
              excluded && 'bg-muted/60 hover:bg-muted/60'
            )

            // Without a drill-down the whole row is the checkbox's label, which
            // makes the entire strip clickable. With one, the name has its own
            // job, so only the checkbox itself selects.
            if (!onOpen) {
              return (
                <label key={row.id} className={cn(rowClass, 'cursor-pointer')}>
                  <Checkbox
                    {...mark}
                    checked={checked}
                    onCheckedChange={(value) => onToggle(row.id, value === true)}
                  />
                  {name}
                  {meta}
                </label>
              )
            }

            return (
              <div key={row.id} className={rowClass}>
                {selectable && (
                  <Checkbox
                    {...mark}
                    checked={checked}
                    aria-label={row.name}
                    onCheckedChange={(value) => onToggle(row.id, value === true)}
                  />
                )}
                {open ? (
                  <button
                    type="button"
                    onClick={() => onOpen(row.id)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 text-left"
                  >
                    {name}
                    <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
                  </button>
                ) : (
                  name
                )}
                {meta}
              </div>
            )
          })}
        {/* The footer says what lies beyond the rows, so it waits for the last of them. */}
        {!loading && allRendered && footer}
      </div>
    </div>
  )
}
